/**
 * meta/cosmetic-models.js — the geometry behind every cosmetic (GDD 3.4).
 *
 * `avatar/cosmetics.js` owns the five-slot attach system and asks this module
 * for the object to mount. Builders are registered by id at import time, which
 * is what replaces that module's greybox fallback.
 *
 * Two conventions matter, both inherited from `avatar/cosmetics.js`:
 *
 * - A builder returns an Object3D positioned in the ANCHOR's local space, and
 *   the anchor already sits at the right spot on the goose. Build around origin.
 * - Materials come from the memoised `mat()` cache and are never disposed on
 *   unequip; geometries are freshly allocated and are. So: shared materials,
 *   own geometry. Reusing a cached geometry here would hand a disposed buffer
 *   to the next equip.
 *
 * **Skins are not meshes.** A "skin" recolours the goose itself, so its builder
 * returns an empty group (which stops the greybox fallback from bolting a box
 * to the body anchor) and `cosmetics-catalog.js` does the palette swap.
 *
 * @module meta/cosmetic-models
 */

import * as THREE from 'three';
import { mat } from '../assets3d/factory.js';
import { registerBuilder } from '../avatar/cosmetics.js';

/** Goose-body recolours, applied by cosmetics-catalog.js rather than mounted. */
export const SKIN_PALETTES = Object.freeze({
  'golden-goose-skin': { body: 0xd4af37, beak: 0xfff1a8, wing: 0xe8c86a, metal: 0.75, rough: 0.3 },
  'haunted-goose-skin': { body: 0x9d8bc4, beak: 0x6b5b8a, wing: 0x8878b0, metal: 0.05, rough: 0.9 },
  'snow-goose-skin': { body: 0xeaf4ff, beak: 0xa8c8e8, wing: 0xdcebfa, metal: 0.05, rough: 0.6 },
  'beach-goose-skin': { body: 0xffe4bd, beak: 0xff8c42, wing: 0xffd7a3, metal: 0.05, rough: 0.8 },
});

/** @param {string} id @returns {boolean} */
export function isSkin(id) {
  return Object.prototype.hasOwnProperty.call(SKIN_PALETTES, id);
}

function group(name) {
  const g = new THREE.Group();
  g.name = name;
  return g;
}

function part(geometry, material, cast = true) {
  const m = new THREE.Mesh(geometry, material);
  m.castShadow = cast;
  return m;
}

// ─────────────────────────────────────────────────────────────────────────────
// Head
// ─────────────────────────────────────────────────────────────────────────────

/** Carnival Classic — a striped straw boater (GDD 8.5). */
function barkerHat() {
  const g = group('barker-hat');
  const straw = mat(0xf0dfa8, { rough: 0.9 });

  const brim = part(new THREE.CylinderGeometry(0.155, 0.155, 0.012, 20), straw);
  brim.position.y = 0.035;
  g.add(brim);

  const crown = part(new THREE.CylinderGeometry(0.095, 0.1, 0.085, 20), straw);
  crown.position.y = 0.082;
  g.add(crown);

  const band = part(
    new THREE.CylinderGeometry(0.101, 0.101, 0.03, 20),
    mat(0xe8503a, { rough: 0.7 }),
  );
  band.position.y = 0.055;
  g.add(band);

  const top = part(new THREE.CylinderGeometry(0.095, 0.095, 0.008, 20), straw);
  top.position.y = 0.126;
  g.add(top);
  return g;
}

/** Day-30 login exclusive — a star-topped circlet. */
function starCrown() {
  const g = group('star-crown');
  const gold = mat(0xffd166, { rough: 0.25, metal: 0.8 });

  const band = part(new THREE.TorusGeometry(0.1, 0.014, 8, 20), gold);
  band.rotation.x = Math.PI / 2;
  band.position.y = 0.045;
  g.add(band);

  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    const spike = part(new THREE.ConeGeometry(0.022, 0.075, 5), gold);
    spike.position.set(Math.cos(a) * 0.1, 0.085, Math.sin(a) * 0.1);
    g.add(spike);
  }

  const star = part(
    new THREE.OctahedronGeometry(0.038),
    mat(0xcba6ff, { rough: 0.2, emissive: 0x6a4fa8 }),
  );
  star.position.y = 0.15;
  g.add(star);
  g.userData.animate = (t) => { star.rotation.y = t * 1.4; };
  return g;
}

// ─────────────────────────────────────────────────────────────────────────────
// Body
// ─────────────────────────────────────────────────────────────────────────────

/** Big Top Circus — ringmaster's tailcoat. */
function ringmasterOutfit() {
  const g = group('ringmaster-outfit');
  const red = mat(0x9e2b3a, { rough: 0.7 });
  const gold = mat(0xffd166, { rough: 0.3, metal: 0.7 });

  const vest = part(new THREE.SphereGeometry(0.31, 14, 10), red);
  vest.scale.set(1.02, 0.6, 1.12);
  vest.position.set(0, 0.02, -0.03);
  g.add(vest);

  const lapelL = part(new THREE.BoxGeometry(0.03, 0.16, 0.012), gold);
  lapelL.position.set(-0.07, 0.04, -0.29);
  lapelL.rotation.z = 0.3;
  g.add(lapelL);

  const lapelR = lapelL.clone();
  lapelR.geometry = new THREE.BoxGeometry(0.03, 0.16, 0.012);
  lapelR.position.x = 0.07;
  lapelR.rotation.z = -0.3;
  g.add(lapelR);

  for (const side of [-1, 1]) {
    const epaulette = part(new THREE.CylinderGeometry(0.055, 0.055, 0.022, 12), gold);
    epaulette.position.set(0.24 * side, 0.14, -0.02);
    g.add(epaulette);
  }

  const tails = part(new THREE.ConeGeometry(0.15, 0.34, 10), red);
  tails.rotation.x = Math.PI / 2 - 0.4;
  tails.position.set(0, -0.02, 0.34);
  g.add(tails);
  return g;
}

/** Day-30 login exclusive — a champion's sash with a medal. */
function medalSash() {
  const g = group('medal-sash');
  const ribbon = mat(0x2f6fdb, { rough: 0.75 });

  const sash = part(new THREE.BoxGeometry(0.085, 0.5, 0.02), ribbon);
  sash.position.set(0.02, 0.02, -0.28);
  sash.rotation.set(0.25, 0, 0.55);
  g.add(sash);

  const medal = part(
    new THREE.CylinderGeometry(0.05, 0.05, 0.012, 16),
    mat(0xd4af37, { rough: 0.25, metal: 0.85 }),
  );
  medal.rotation.x = Math.PI / 2;
  medal.position.set(-0.09, -0.13, -0.28);
  g.add(medal);

  const boss = part(
    new THREE.TorusGeometry(0.032, 0.008, 6, 14),
    mat(0xffd166, { rough: 0.3, metal: 0.7 }),
  );
  boss.position.set(-0.09, -0.13, -0.29);
  g.add(boss);
  return g;
}

// ─────────────────────────────────────────────────────────────────────────────
// Feet (the pet slot)
// ─────────────────────────────────────────────────────────────────────────────

/** Plush Paradise — a giant plush goose that waddles along at your feet. */
function giantPlushGoose() {
  const g = group('giant-plush-goose');
  const plush = mat(0xffc2d6, { rough: 0.98 });
  const beakMat = mat(0xf5a623, { rough: 0.6 });

  const pet = group('plush-goose-body');
  pet.position.set(0.42, 0, 0.16);

  const body = part(new THREE.SphereGeometry(0.16, 12, 10), plush);
  body.scale.set(1, 0.85, 1.3);
  body.position.y = 0.19;
  pet.add(body);

  const neck = part(new THREE.CylinderGeometry(0.04, 0.055, 0.2, 8), plush);
  neck.position.set(0, 0.33, -0.06);
  neck.rotation.x = -0.2;
  pet.add(neck);

  const head = part(new THREE.SphereGeometry(0.062, 10, 8), plush);
  head.position.set(0, 0.44, -0.1);
  pet.add(head);

  const beak = part(new THREE.ConeGeometry(0.03, 0.1, 7), beakMat);
  beak.rotation.x = -Math.PI / 2;
  beak.position.set(0, 0.435, -0.19);
  pet.add(beak);

  for (const side of [-1, 1]) {
    const eye = part(new THREE.SphereGeometry(0.012, 6, 5), mat(0x14161f, { rough: 0.3 }), false);
    eye.position.set(0.03 * side, 0.465, -0.155);
    pet.add(eye);

    const foot = part(new THREE.BoxGeometry(0.06, 0.018, 0.09), beakMat);
    foot.position.set(0.055 * side, 0.009, 0.01);
    pet.add(foot);
  }

  g.add(pet);
  // A slow bob so it reads as alive rather than as scenery stuck to your ankle.
  g.userData.animate = (t) => {
    pet.position.y = Math.abs(Math.sin(t * 2.2)) * 0.035;
    pet.rotation.z = Math.sin(t * 2.2) * 0.07;
  };
  return g;
}

// ─────────────────────────────────────────────────────────────────────────────
// Aura
// ─────────────────────────────────────────────────────────────────────────────

/** Neon Nights — a stacked pair of counter-rotating neon rings. */
function neonGlow() {
  const g = group('neon-glow');
  const rings = [];
  const colors = [0x39d7ff, 0xff45c8];

  for (let i = 0; i < 2; i++) {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(0.44 - i * 0.06, 0.018, 8, 28),
      new THREE.MeshBasicMaterial({ color: colors[i], transparent: true, opacity: 0.75 }),
    );
    ring.rotation.x = Math.PI / 2;
    ring.position.y = -0.1 + i * 0.22;
    g.add(ring);
    rings.push(ring);
  }

  const glow = new THREE.Mesh(
    new THREE.SphereGeometry(0.42, 14, 10),
    new THREE.MeshBasicMaterial({ color: 0x39d7ff, transparent: true, opacity: 0.07 }),
  );
  g.add(glow);

  g.userData.animate = (t) => {
    rings[0].rotation.z = t * 1.1;
    rings[1].rotation.z = -t * 0.8;
    const pulse = 0.6 + Math.sin(t * 2.4) * 0.2;
    rings[0].material.opacity = pulse;
    rings[1].material.opacity = pulse * 0.9;
    glow.material.opacity = 0.05 + Math.sin(t * 2.4) * 0.03;
  };
  return g;
}

/** Day-30 login exclusive — orbiting comet sparks. */
function cometAura() {
  const g = group('comet-aura');
  const sparks = [];
  const count = 7;

  for (let i = 0; i < count; i++) {
    const spark = new THREE.Mesh(
      new THREE.SphereGeometry(0.026 - (i % 3) * 0.006, 7, 6),
      new THREE.MeshBasicMaterial({
        color: i % 2 ? 0xffd166 : 0xcba6ff,
        transparent: true,
        opacity: 0.9,
      }),
    );
    g.add(spark);
    sparks.push(spark);
  }

  g.userData.animate = (t) => {
    for (let i = 0; i < sparks.length; i++) {
      const a = t * 1.5 + (i / sparks.length) * Math.PI * 2;
      const r = 0.4 + Math.sin(t * 1.2 + i) * 0.05;
      sparks[i].position.set(Math.cos(a) * r, Math.sin(t * 1.8 + i * 0.9) * 0.22, Math.sin(a) * r);
      sparks[i].material.opacity = 0.55 + Math.sin(t * 3 + i) * 0.35;
    }
  };
  return g;
}

/** Skins mount nothing — the recolour is the cosmetic. */
function emptySkinMount() {
  return group('skin-mount');
}

// ─────────────────────────────────────────────────────────────────────────────
// Registration
// ─────────────────────────────────────────────────────────────────────────────

/** id → builder, exported so a test (or the wardrobe preview) can build one. */
export const BUILDERS = Object.freeze({
  'carnival-barker-hat': barkerHat,
  'neon-goose-glow': neonGlow,
  'ringmaster-outfit': ringmasterOutfit,
  'giant-plush-goose': giantPlushGoose,
  'golden-goose-skin': emptySkinMount,
  'haunted-goose-skin': emptySkinMount,
  'snow-goose-skin': emptySkinMount,
  'beach-goose-skin': emptySkinMount,
  'login-star-crown': starCrown,
  'login-comet-aura': cometAura,
  'login-medal-sash': medalSash,
});

/** Register every builder with the avatar attach system. Idempotent. */
export function installCosmeticModels() {
  for (const [id, build] of Object.entries(BUILDERS)) registerBuilder(id, build);
  return Object.keys(BUILDERS).length;
}

installCosmeticModels();
